import specialtyData from './specialtyMealPlans.json';
import { buildWeeklyMenu, expandToFullWeek } from './mealSlotTypes';
import {
  FaSun, FaCloudSun, FaUtensils, FaCookieBite, FaMoon, FaLayerGroup,
  FaDumbbell, FaHeartbeat, FaWeight, FaHome,
} from 'react-icons/fa';

export { buildWeeklyMenu } from './mealSlotTypes';

/** Individual delivery time slots (from Excel). */
export const EXTENDED_MEAL_SLOTS = [
  { id: 'breakfast', label: 'Breakfast', excelSlot: 'Bf', icon: FaSun, time: '8:00 AM – 10:00 AM' },
  { id: 'mid_morning', label: 'Mid Mor Snack', excelSlot: 'Mid Mor Snack', icon: FaCloudSun, time: '10:30 AM – 11:30 AM' },
  { id: 'lunch', label: 'Lunch', excelSlot: 'Lunch', icon: FaUtensils, time: '12:00 PM – 2:00 PM' },
  { id: 'evening_snack', label: 'Evng Snack', excelSlot: 'Evng Snack', icon: FaCookieBite, time: '4:30 PM – 5:30 PM' },
  { id: 'dinner', label: 'Dinner', excelSlot: 'Dinner', icon: FaMoon, time: '7:00 PM – 9:00 PM' },
];

/** Customer-facing Choose Meals options (mid-morning / evening only via Full Day). */
export const FULL_DAY_UI_SLOT_ID = 'full_day';
export const FULL_DAY_INTERNAL_SLOT_IDS = EXTENDED_MEAL_SLOTS.map((slot) => slot.id);
export const SELECTABLE_BUILDER_SLOTS = [
  EXTENDED_MEAL_SLOTS[0], // breakfast
  EXTENDED_MEAL_SLOTS[2], // lunch
  EXTENDED_MEAL_SLOTS[4], // dinner
  {
    id: FULL_DAY_UI_SLOT_ID,
    label: 'Full Day',
    excelSlot: 'Full Day',
    icon: FaLayerGroup,
    time: 'All five meal slots',
  },
];

export function makeFullDayGroupId(planId, organic = false) {
  return `fd:${String(planId || 'plan')}:${organic ? 1 : 0}:${Date.now()}:${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Group draft lines that belong to a Full Day package add.
 * @returns {Array<{ groupId: string, planId: string, organic: boolean, quantity: number, regionKey: string|null, itemsBySlot: Record<string, object> }>}
 */
export function collectFullDayPackageGroups(slotDraftProducts = {}) {
  /** @type {Map<string, { groupId: string, planId: string, organic: boolean, quantity: number, regionKey: string|null, itemsBySlot: Record<string, object> }>} */
  const groups = new Map();
  FULL_DAY_INTERNAL_SLOT_IDS.forEach((slotId) => {
    (slotDraftProducts[slotId] || []).forEach((item) => {
      if (!item?.isFullDayPackage || !item.fullDayGroupId) return;
      const groupId = String(item.fullDayGroupId);
      const itemRegionKey = String(item?.regionKey || '').toLowerCase();
      const regionKey = GENERAL_REGION_KEYS.includes(itemRegionKey) ? itemRegionKey : null;
      if (!groups.has(groupId)) {
        groups.set(groupId, {
          groupId,
          planId: String(item.planId || parseSpecialtySlotProductId(item.id)?.planId || ''),
          organic: Boolean(item.isOrganic),
          quantity: Math.max(1, Number(item.quantity) || 1),
          regionKey,
          itemsBySlot: {},
        });
      }
      const group = groups.get(groupId);
      group.itemsBySlot[slotId] = item;
      group.quantity = Math.max(group.quantity, Math.max(1, Number(item.quantity) || 1));
      if (!group.planId) {
        group.planId = String(item.planId || parseSpecialtySlotProductId(item.id)?.planId || '');
      }
      if (!group.regionKey && regionKey) {
        group.regionKey = regionKey;
      }
    });
  });
  return [...groups.values()].filter((group) => Object.keys(group.itemsBySlot).length > 0);
}

/** UI-facing slot codes for receipts/admin (full_day + standalone breakfast/lunch/dinner). */
export function computeUiSlotCodes(slotDraftProducts = {}) {
  const codes = [];
  const groups = collectFullDayPackageGroups(slotDraftProducts);
  if (groups.length) codes.push(FULL_DAY_UI_SLOT_ID);
  ['breakfast', 'lunch', 'dinner'].forEach((slotId) => {
    const hasStandalone = (slotDraftProducts[slotId] || []).some((item) => !item?.isFullDayPackage);
    if (hasStandalone) codes.push(slotId);
  });
  return codes;
}

export function uiSlotCodeLabel(code) {
  if (code === FULL_DAY_UI_SLOT_ID) return 'Full Day';
  return EXTENDED_MEAL_SLOTS.find((s) => s.id === code)?.label || String(code || '');
}

/** Legacy quick picks (still supported). */
export const LEGACY_MEAL_PRESETS = [
  { id: 'lunch', label: 'Lunch', slotIds: ['lunch'] },
  { id: 'dinner', label: 'Dinner', slotIds: ['dinner'] },
  { id: 'both', label: 'Lunch + Dinner', slotIds: ['lunch', 'dinner'] },
];

/** Which Excel pricing bundle covers which slot ids. */
export const BUNDLE_SLOT_MAP = {
  'Complete Meal': ['breakfast', 'mid_morning', 'lunch', 'evening_snack', 'dinner'],
  'Complete weekly plan': ['breakfast', 'mid_morning', 'lunch', 'evening_snack', 'dinner'],
  'Breakfast': ['breakfast'],
  'Mid Mor Snack': ['mid_morning'],
  'Bf N Mrng Snack': ['breakfast', 'mid_morning'],
  'Bf N Snack': ['breakfast', 'mid_morning'],
  'Lunch': ['lunch'],
  'Lunch N Evng Snack': ['lunch', 'evening_snack'],
  'Lunch N Snack': ['lunch', 'evening_snack'],
  'Evng Snack': ['evening_snack'],
  'Dinner': ['dinner'],
};

/** Display label + fixed off-% when the full-day (all menu slots) package is selected. */
export const FULL_DAY_MEAL_LABEL = 'Full Day Meal';
export const FULL_DAY_MEAL_DISCOUNT_PERCENT = 20;

export function isFullDayMealBundleName(bundle) {
  return /complete\s*(meal|weekly\s*plan)/i.test(String(bundle || ''))
    || String(bundle || '').trim().toLowerCase() === FULL_DAY_MEAL_LABEL.toLowerCase();
}

export function getFullDayMealDiscountPercent(plan) {
  const configured = Number(plan?.fullDayMealDiscountPercent);
  if (!Number.isFinite(configured)) return FULL_DAY_MEAL_DISCOUNT_PERCENT;
  return Math.min(100, Math.max(0, configured));
}

function applyFullDayMealDiscount(slotsTotal, plan) {
  if (slotsTotal == null || !Number.isFinite(Number(slotsTotal))) return null;
  return Math.round(Number(slotsTotal) * (1 - getFullDayMealDiscountPercent(plan) / 100));
}

const PLAN_ID_OFFSET = { general: 0, gym: 15, diabetic: 35, weightloss: 55 };

export const GENERAL_REGION_KEYS = ['south', 'north'];

const ORGANIC_ONLY_PLAN_IDS = new Set(['weightloss']);

/** Plans that only offer organic meals (no Regular pricing tab). */
export function isOrganicOnlySpecialtyPlan(planOrId) {
  if (planOrId && typeof planOrId === 'object') {
    if (planOrId.organicOnly) return true;
  }
  const id = typeof planOrId === 'string' ? planOrId : planOrId?.id;
  return ORGANIC_ONLY_PLAN_IDS.has(id);
}

function normalizeWeeklyMenu(source) {
  return expandToFullWeek(buildWeeklyMenu(source));
}

function withWeeklyMenu(plan) {
  if (!plan) return null;

  if (plan.regionalMenus) {
    const regionalMenus = {};
    for (const key of GENERAL_REGION_KEYS) {
      const region = plan.regionalMenus[key];
      if (!region) continue;
      regionalMenus[key] = {
        ...region,
        weeklyMenu: normalizeWeeklyMenu({ weeklyMenu: region.weeklyMenu }),
      };
    }
    return {
      ...plan,
      regionalMenus,
      weeklyMenu: regionalMenus.south?.weeklyMenu || normalizeWeeklyMenu(plan),
    };
  }

  const weeklyMenu = normalizeWeeklyMenu(plan);
  return { ...plan, weeklyMenu };
}

const WEIGHTLOSS_PLAN = {
  id: 'weightloss',
  name: 'Weight Loss & PCOD',
  tagline: 'Calorie-conscious rotating weekly menus',
  icon: 'weightloss',
  pricing: [
    {
      bundle: 'Complete Meal',
      perDayNonOrganic: 699,
      perDayOrganic: 999,
      exclGstNonOrganic: 666,
      exclGstOrganic: 952,
    },
    {
      bundle: 'Breakfast',
      perDayNonOrganic: 179,
      perDayOrganic: 269,
      exclGstNonOrganic: 170.48,
      exclGstOrganic: 256.19,
    },
    {
      bundle: 'Mid Mor Snack',
      perDayNonOrganic: 99,
      perDayOrganic: 149,
      exclGstNonOrganic: 94.29,
      exclGstOrganic: 141.9,
    },
    {
      bundle: 'Lunch',
      perDayNonOrganic: 279,
      perDayOrganic: 399,
      exclGstNonOrganic: 265.71,
      exclGstOrganic: 380.0,
    },
    {
      bundle: 'Evng Snack',
      perDayNonOrganic: 99,
      perDayOrganic: 149,
      exclGstNonOrganic: 94.29,
      exclGstOrganic: 141.9,
    },
    {
      bundle: 'Dinner',
      perDayNonOrganic: 249,
      perDayOrganic: 369,
      exclGstNonOrganic: 237.14,
      exclGstOrganic: 351.43,
    },
    {
      bundle: 'Bf N Mrng Snack',
      perDayNonOrganic: 249,
      perDayOrganic: 379,
      exclGstNonOrganic: 237.14,
      exclGstOrganic: 360.95,
    },
    {
      bundle: 'Lunch N Evng Snack',
      perDayNonOrganic: 349,
      perDayOrganic: 499,
      exclGstNonOrganic: 332.38,
      exclGstOrganic: 475.24,
    },
  ],
  sampleWeeks: [
    {
      name: 'Week 1 — Diet special meal',
      sections: [
        {
          title: 'Lunch (Mon–Sat)',
          days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          menus: {
            Monday: 'Quinoa vegetable pulao, mixed salad, low-fat curd (~370 kcal)',
            Tuesday: 'Button idli, tomato chutney, kosambari (~340 kcal)',
            Wednesday: 'Rajma with red rice, cabbage & peas salad (~360 kcal)',
            Thursday: 'Brown rice & sambar, sprouted green gram salad (~380 kcal)',
            Friday: 'Eggplant curry with millets & mixed salad (~450 kcal)',
            Saturday: 'Chapati, palak paneer, carrot-beetroot salad (~400 kcal)',
          },
        },
        {
          title: 'Afternoon snack',
          days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          menus: {
            Monday: 'Oats pancakes (~100 kcal)',
            Tuesday: 'Masala roasted chickpeas (~100 kcal)',
            Wednesday: 'Jowar pancakes (~100 kcal)',
            Thursday: 'Vegetable tikki (~100 kcal)',
            Friday: 'Gunta ponganalu (~100 kcal)',
            Saturday: 'Nuts (~100 kcal)',
          },
        },
        {
          title: 'Dinner',
          days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          menus: {
            Monday: 'Mixed vegetable jowar upma, achaar, low-fat curd (~400 kcal)',
            Tuesday: 'Chapati with chole & clear soup (~450 kcal)',
            Wednesday: 'Mixed vegetable poha & buttermilk (~400 kcal)',
            Thursday: 'Tofu stir-fry with sautéed spinach (~350 kcal)',
            Friday: 'Grilled vegetables with steamed broccoli (~250 kcal)',
            Saturday: 'Mushroom brown rice pulao with raita (~350 kcal)',
          },
        },
      ],
    },
    {
      name: 'Week 2 & 3',
      sections: [
        {
          title: 'Rotating menus',
          days: [],
          menus: {},
          description: 'Additional weekly menus are provided on the Weight Loss & PCOD track. Our team shares the full rotation when you subscribe.',
        },
      ],
    },
  ],
};

export const SPECIALTY_PLAN_CATALOG = [
  withWeeklyMenu({
    ...specialtyData.plans.find((p) => p.id === 'general'),
    tagline: 'South & North Indian homestyle meals — organic & non-organic',
    icon: 'general',
    iconComponent: FaHome,
    accent: '#7c3aed',
  }),
  withWeeklyMenu({
    ...specialtyData.plans.find((p) => p.id === 'gym'),
    tagline: 'High-protein vegetarian meals for active lifestyles',
    icon: 'gym',
    iconComponent: FaDumbbell,
    accent: '#2563eb',
  }),
  withWeeklyMenu({
    ...specialtyData.plans.find((p) => p.id === 'diabetic'),
    tagline: 'Low-GI meals tailored for blood sugar balance',
    icon: 'diabetic',
    iconComponent: FaHeartbeat,
    accent: '#059669',
  }),
  withWeeklyMenu({
    ...WEIGHTLOSS_PLAN,
    iconComponent: FaWeight,
    accent: '#c45c26',
  }),
].filter(Boolean);

export function getSpecialtyPlanById(id) {
  return SPECIALTY_PLAN_CATALOG.find((p) => p.id === id) || null;
}

/** Plan with regional weekly menu applied (e.g. General → South/North). */
export function resolveSpecialtyPlanForRegion(plan, regionKey = 'south') {
  if (!plan) return null;
  if (plan.regionalMenus?.[regionKey]) {
    const region = plan.regionalMenus[regionKey];
    return {
      ...plan,
      weeklyMenu: region.weeklyMenu,
      activeRegionLabel: region.label,
    };
  }
  return plan;
}

/** Delivery slot ids that have at least one meal in the plan weekly menu. */
export function getPlanMenuSlotIds(plan) {
  if (!plan?.weeklyMenu?.length) return [];
  const ids = new Set();
  for (const dayRow of plan.weeklyMenu) {
    for (const meal of dayRow.meals || []) {
      if (meal?.slotId) ids.add(meal.slotId);
    }
  }
  return EXTENDED_MEAL_SLOTS.filter((slot) => ids.has(slot.id)).map((slot) => slot.id);
}

const SPECIALTY_SLOT_PRODUCT_BASE = 6000;

const SLOT_ID_INDEX = {
  breakfast: 1,
  mid_morning: 2,
  lunch: 3,
  evening_snack: 4,
  dinner: 5,
};

/** Stable numeric product id for per-slot specialty items (backend requires numeric ids). */
export function getSpecialtySlotProductId(planId, slotId) {
  const planOffset = PLAN_ID_OFFSET[planId] ?? 60;
  const slotIndex = SLOT_ID_INDEX[slotId];
  if (!slotIndex) return null;
  return SPECIALTY_SLOT_PRODUCT_BASE + planOffset * 10 + slotIndex;
}

export function parseSpecialtySlotProductId(productId) {
  const raw = String(productId ?? '');

  if (raw.startsWith('specialty-')) {
    const rest = raw.slice('specialty-'.length);
    const slotIds = EXTENDED_MEAL_SLOTS.map((slot) => slot.id).sort((a, b) => b.length - a.length);
    for (const slotId of slotIds) {
      const suffix = `-${slotId}`;
      if (rest.endsWith(suffix)) {
        const planId = rest.slice(0, -suffix.length);
        if (planId) return { planId, slotId };
      }
    }
    return null;
  }

  const numeric = Number(productId);
  if (!Number.isFinite(numeric) || numeric < SPECIALTY_SLOT_PRODUCT_BASE) return null;

  const code = numeric - SPECIALTY_SLOT_PRODUCT_BASE;
  const slotIndex = code % 10;
  const planOffset = Math.floor(code / 10);
  const planId = Object.entries(PLAN_ID_OFFSET).find(([, value]) => value === planOffset)?.[0];
  const slotId = Object.entries(SLOT_ID_INDEX).find(([, value]) => value === slotIndex)?.[0];
  if (!planId || !slotId) return null;
  return { planId, slotId };
}

export function isSpecialtySlotProductId(productId) {
  return parseSpecialtySlotProductId(productId) != null;
}

export function slotsToLabel(slotIds) {
  return slotIds
    .map((id) => EXTENDED_MEAL_SLOTS.find((s) => s.id === id)?.label || id)
    .join(', ');
}

function setsEqual(a, b) {
  if (a.length !== b.length) return false;
  const sa = new Set(a);
  return b.every((x) => sa.has(x));
}

/** Per-slot pricing row (Breakfast, Lunch, etc.). */
export function getSlotPricingRow(plan, slotId) {
  if (!plan?.pricing) return null;
  for (const row of plan.pricing) {
    const bundleSlots = BUNDLE_SLOT_MAP[row.bundle];
    if (bundleSlots?.length === 1 && bundleSlots[0] === slotId) return row;
  }
  return null;
}

export function resolveSelectedPackageLabel(plan, slotIds, organic = false) {
  if (!slotIds?.length) {
    return 'Select meal slots in the subscription builder';
  }
  const sorted = [...slotIds].sort();
  const pkg = findExactPackagePrice(plan, sorted);
  if (pkg?.bundle) {
    return `${organic ? 'Organic' : 'Non-organic'} — ${pkg.bundle}`;
  }
  return `${organic ? 'Organic' : 'Non-organic'} — ${slotsToLabel(slotIds)}`;
}

export function resolveSpecialtyPerDayPrice(plan, slotIds, organic = false) {
  if (!plan?.pricing?.length || !slotIds?.length) return null;

  const sorted = [...slotIds].sort();
  const pkg = findExactPackagePrice(plan, sorted);
  if (pkg) {
    const bundled = organic ? pkg.organic : pkg.regular;
    if (bundled != null) return bundled;
  }

  // Partial selection: sum per-slot prices (never apply a larger bundle that merely contains the slots).
  return sumIndividualSlotPrices(plan, sorted, organic);
}

/**
 * Charge for a slot selection: sum individual slot prices, capped at the matching
 * bundle price when the sum exceeds it, and at the full-menu package when applicable.
 */
export function resolveAddedSlotsChargePerDay(plan, slotIds, menuSlotIds, organic = false) {
  if (!plan?.pricing?.length || !slotIds?.length) return null;

  const sorted = [...slotIds].sort();
  const slotsTotal = sumIndividualSlotPrices(plan, sorted, organic);
  const lineSum = slotsTotal ?? resolveSpecialtyPerDayPrice(plan, sorted, organic);
  if (lineSum == null) return null;

  let charge = lineSum;

  const exactPkg = findExactPackagePrice(plan, sorted);
  if (exactPkg) {
    const bundlePrice = organic ? exactPkg.organic : exactPkg.regular;
    if (lineSum > bundlePrice) charge = bundlePrice;
  }

  const fullMenuIds = menuSlotIds?.length ? menuSlotIds : getPlanMenuSlotIds(plan);
  if (fullMenuIds?.length) {
    const fullPkg = findExactPackagePrice(plan, fullMenuIds);
    const fullPackagePrice = fullPkg
      ? (organic ? fullPkg.organic : fullPkg.regular)
      : resolveSpecialtyPerDayPrice(plan, fullMenuIds, organic);
    if (fullPackagePrice != null && charge > fullPackagePrice) {
      charge = fullPackagePrice;
    }
  }

  return charge;
}

function sumIndividualSlotPrices(plan, slotIds, organic) {
  let sum = 0;
  let matched = 0;
  for (const slotId of slotIds) {
    const row = getSlotPricingRow(plan, slotId);
    if (!row) continue;
    sum += organic ? row.perDayOrganic : row.perDayNonOrganic;
    matched += 1;
  }
  return matched === slotIds.length ? sum : null;
}

export function findExactPackagePrice(plan, slotIds) {
  const sorted = [...slotIds].sort();
  for (const row of plan.pricing || []) {
    const bundleSlots = BUNDLE_SLOT_MAP[row.bundle];
    if (bundleSlots && setsEqual([...bundleSlots].sort(), sorted)) {
      if (isFullDayMealBundleName(row.bundle)) {
        const regularTotal = sumIndividualSlotPrices(plan, sorted, false);
        const organicTotal = sumIndividualSlotPrices(plan, sorted, true);
        return {
          bundle: FULL_DAY_MEAL_LABEL,
          regular: applyFullDayMealDiscount(regularTotal, plan) ?? row.perDayNonOrganic,
          organic: applyFullDayMealDiscount(organicTotal, plan) ?? row.perDayOrganic,
        };
      }
      return {
        bundle: row.bundle,
        regular: row.perDayNonOrganic,
        organic: row.perDayOrganic,
      };
    }
  }

  // Full menu selection without a matching catalog bundle row → still 20% Full Day Meal.
  const menuIds = getPlanMenuSlotIds(plan);
  if (menuIds?.length && setsEqual([...menuIds].sort(), sorted)) {
    const regularTotal = sumIndividualSlotPrices(plan, sorted, false);
    const organicTotal = sumIndividualSlotPrices(plan, sorted, true);
    const regular = applyFullDayMealDiscount(regularTotal, plan);
    const organic = applyFullDayMealDiscount(organicTotal, plan);
    if (regular != null || organic != null) {
      return {
        bundle: FULL_DAY_MEAL_LABEL,
        regular,
        organic,
      };
    }
  }
  return null;
}

function buildPriceSide(slotsTotal, packageInfo) {
  const packagePrice = packageInfo?.price ?? null;
  const packageName = packageInfo?.bundle ?? null;

  if (slotsTotal == null && packagePrice == null) {
    return { display: null, slotsTotal: null, packagePrice: null, packageName: null, showPackageDeal: false };
  }

  if (packagePrice != null && slotsTotal != null && slotsTotal > packagePrice) {
    return {
      display: packagePrice,
      slotsTotal,
      packagePrice,
      packageName,
      showPackageDeal: true,
    };
  }

  const display = packagePrice != null ? packagePrice : slotsTotal;
  return {
    display,
    slotsTotal,
    packagePrice,
    packageName,
    showPackageDeal: false,
  };
}

const ALL_SLOT_IDS = ['breakfast', 'mid_morning', 'lunch', 'evening_snack', 'dinner'];

/** Per-slot Reg/Org prices for every delivery slot (independent of selection). */
export function getAllSlotPrices(plan) {
  const slotPrices = {};
  if (!plan?.pricing) return slotPrices;
  for (const slotId of ALL_SLOT_IDS) {
    const row = getSlotPricingRow(plan, slotId);
    if (row) {
      slotPrices[slotId] = {
        regular: row.perDayNonOrganic,
        organic: row.perDayOrganic,
      };
    }
  }
  return slotPrices;
}

/**
 * Per-slot prices, slot totals, and package comparison for the select-plan UI.
 */
/** Per-day package price, à la carte total, and savings for a fixed slot set (e.g. full menu package). */
export function resolveFullPackageDayPricing(plan, slotIds, organic = false) {
  const pricing = resolveSlotSelectionPricing(plan, slotIds);
  const side = organic ? pricing.organic : pricing.regular;
  const chargePerDay = organic ? pricing.chargeOrganic : pricing.chargeRegular;
  const savingsPerDay = side?.showPackageDeal && side.slotsTotal != null && chargePerDay != null
    ? Math.max(0, side.slotsTotal - chargePerDay)
    : 0;

  return {
    chargePerDay,
    slotsTotalPerDay: side?.slotsTotal ?? null,
    packageName: side?.packageName ?? null,
    showPackageDeal: Boolean(side?.showPackageDeal),
    savingsPerDay,
  };
}

export function resolveSlotSelectionPricing(plan, slotIds) {
  const slotPrices = getAllSlotPrices(plan);

  if (!plan?.pricing?.length || !slotIds?.length) {
    return {
      slotPrices,
      regular: buildPriceSide(null, null),
      organic: buildPriceSide(null, null),
      chargeRegular: null,
      chargeOrganic: null,
    };
  }

  const sorted = [...slotIds].sort();
  const pkg = findExactPackagePrice(plan, sorted);
  const slotsTotalRegular = sumIndividualSlotPrices(plan, sorted, false);
  const slotsTotalOrganic = sumIndividualSlotPrices(plan, sorted, true);
  const menuSlotIds = getPlanMenuSlotIds(plan);

  return {
    slotPrices,
    regular: buildPriceSide(slotsTotalRegular, pkg ? { bundle: pkg.bundle, price: pkg.regular } : null),
    organic: buildPriceSide(slotsTotalOrganic, pkg ? { bundle: pkg.bundle, price: pkg.organic } : null),
    chargeRegular: resolveAddedSlotsChargePerDay(plan, sorted, menuSlotIds, false),
    chargeOrganic: resolveAddedSlotsChargePerDay(plan, sorted, menuSlotIds, true),
  };
}

/** Virtual add-on products for specialty pricing bundles (add-product modal). */
export function getSpecialtyBundleProducts(plan) {
  if (!plan?.pricing) return [];
  return plan.pricing.map((row, index) => ({
    id: 5000 + index + (PLAN_ID_OFFSET[plan.id] ?? 60),
    name: `${plan.name} — ${row.bundle}`,
    price: row.perDayNonOrganic,
    priceOrganic: row.perDayOrganic,
    category: 'Specialty plans',
    planId: plan.id,
    bundle: row.bundle,
    slotIds: BUNDLE_SLOT_MAP[row.bundle] || [],
    description: `Per day (non-organic ₹${row.perDayNonOrganic} · organic ₹${row.perDayOrganic}). Excl. GST: ₹${row.exclGstNonOrganic} / ₹${row.exclGstOrganic}.`,
    image: null,
    isSpecialtyBundle: true,
  }));
}

export function getSpecialtySlotMeals(plan, slotId) {
  if (!plan?.weeklyMenu?.length || !slotId) return [];
  return plan.weeklyMenu
    .map((dayRow) => {
      const meal = dayRow.meals?.find((item) => item.slotId === slotId);
      return meal ? { day: dayRow.day, ...meal } : null;
    })
    .filter(Boolean);
}

export function syncLegacyPresetFromSlots(slotIds) {
  const sorted = [...slotIds].sort().join(',');
  const lunchDinner = ['dinner', 'lunch'].sort().join(',');
  if (sorted === 'lunch') return 'lunch';
  if (sorted === 'dinner') return 'dinner';
  if (sorted === lunchDinner) return 'both';
  return null;
}

export function slotsFromLegacyPreset(presetId) {
  const preset = LEGACY_MEAL_PRESETS.find((p) => p.id === presetId);
  return preset ? [...preset.slotIds] : ['lunch'];
}
